/**
 * applyOptimisticPlanned (#1315) — fold a planned-transaction edit into
 * the cached ``liquiditaet_planned_transaction`` derived-fact list. The
 * hook's series memo re-runs on the updated cache, so the curve + verdict
 * recompute *before* the server acks — the "instant recompute" demo beat.
 * A present ``derived_fact_id`` replaces in place (edit/move); an absent
 * one appends under a temp id that the on-settle refetch reconciles.
 */
import type { DerivedFact, DerivedFactListResponse } from '../../../services/twinService';
import type { LedgerCategory } from './ledgerCategory';

export interface PlannedTransactionInput {
  derived_fact_id?: string;
  direction: 'income' | 'expense';
  /** Brutto total (= ``qty × unit_price_eur``). */
  amount_eur: number;
  /** Volume/quantity behind the total; ``1`` when the line has no natural quantity. */
  qty?: number;
  /** Price per unit; ``= amount_eur`` when ``qty`` is 1. */
  unit_price_eur?: number;
  value_date: string;
  period_ym: string;
  label: string;
  /** Categorisation on the 2-level GuV ledger_category axis; defaults to ``unsorted``. */
  ledger_category?: LedgerCategory;
  /** Free-text note (rationale / deviation). */
  notes?: string;
  qty_meta?: { ha?: number; t?: number; eur_per_t?: number };
  /** The owning bank account (#2211). Multi-account tenants always send it (the
   *  modal pre-selects it); single-account tenants omit it so the key stays absent
   *  on the wire. Threaded into the optimistic fact so a create/reassign routes to
   *  the right Konto's series BEFORE the server acks (the instant-recompute beat). */
  account_id?: string;
  /** The Gegenpartei this transaction is with (#2678 c) — an ``organization``
   *  entity id the farmer picked. OPTIONAL and farmer-authored: never inferred
   *  from ``counterparty`` text or ``counterparty_iban`` (see ``counterparty.ts``
   *  for why #2741/#2736 make inference actively harmful here). Conditional key
   *  exactly like ``account_id`` above — an unpicked Gegenpartei keeps the key
   *  ABSENT (not null), which is both what ``extra="forbid"`` expects on the wire
   *  and what the server writes back, so the optimistic fact and the round-trip
   *  agree byte for byte. */
  counterparty_id?: string;
}

// Each create gets its OWN temp id. A shared constant would make a second
// create (double-click / slow network, before the first refetch settles)
// overwrite the first one in the cache, dropping it from the curve + ledger.
// The id is client-only — the create POST sends no id. The hook mints it in
// onMutate (so onSuccess can reconcile it to the server id via
// `reconcilePlannedId`), then the on-settle invalidate refetches the canonical
// row. Exported because the hook needs to mint + track the id, not just here.
export function optimisticId(): string {
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `liq-plan-optimistic-${rand}`;
}

function toFact(input: PlannedTransactionInput, tempId?: string): DerivedFact {
  return {
    derived_fact_id: input.derived_fact_id ?? tempId ?? optimisticId(),
    entity_type: 'liquiditaet',
    entity_id: 'liquiditaet',
    fact_type: 'liquiditaet_planned_transaction',
    fact_value: {
      kind: 'planned',
      direction: input.direction,
      amount_eur: input.amount_eur,
      ...(input.qty != null ? { qty: input.qty } : {}),
      ...(input.unit_price_eur != null ? { unit_price_eur: input.unit_price_eur } : {}),
      value_date: input.value_date,
      period_ym: input.period_ym,
      label: input.label,
      ledger_category: input.ledger_category ?? 'unsorted',
      ...(input.notes ? { notes: input.notes } : {}),
      ...(input.qty_meta ? { qty_meta: input.qty_meta } : {}),
      // #2211: conditional key — an account-less (single-account) create keeps the
      // key ABSENT (not null), so the optimistic fact matches the server round-trip.
      ...(input.account_id ? { account_id: input.account_id } : {}),
      // #2678 c: same conditional key — an unpicked (or cleared) Gegenpartei keeps
      // the key ABSENT, so the optimistic fact matches the server round-trip.
      ...(input.counterparty_id ? { counterparty_id: input.counterparty_id } : {}),
      source: 'user',
    },
    rule_id: 'user.liquiditaet_edit',
    computed_at: '',
  };
}

export function applyOptimisticPlanned(
  old: DerivedFactListResponse | undefined,
  input: PlannedTransactionInput,
  tempId?: string,
): DerivedFactListResponse {
  const fact = toFact(input, tempId);
  const base = old?.derived_facts ?? [];
  const idx = base.findIndex((f) => f.derived_fact_id === fact.derived_fact_id);
  const derived_facts =
    idx >= 0
      ? base.map((f, i) => (i === idx ? fact : f))
      : [...base, fact];

  return {
    derived_facts,
    total: derived_facts.length,
    page: old?.page ?? 1,
    limit: old?.limit ?? 1000,
    has_more: old?.has_more ?? false,
  };
}

/**
 * Swap a create's client-only temp id for the server-assigned id once the
 * POST acks, so an edit/move issued before the on-settle refetch targets the
 * real row. Without this the edit would POST the ``liq-plan-optimistic-*`` id
 * — which the backend's ``liq-plan-*`` id filter accepts — and persist a
 * SECOND planned fact instead of updating the server-created one. Returns the
 * same reference unchanged when there is nothing to reconcile (no cache, id
 * absent, or temp == server) so callers can skip a pointless cache write.
 */
export function reconcilePlannedId(
  old: DerivedFactListResponse | undefined,
  tempId: string,
  serverId: string,
): DerivedFactListResponse | undefined {
  if (!old || tempId === serverId) return old;
  let changed = false;
  const derived_facts = old.derived_facts.map((f) => {
    if (f.derived_fact_id === tempId) {
      changed = true;
      return { ...f, derived_fact_id: serverId };
    }
    return f;
  });
  return changed ? { ...old, derived_facts } : old;
}
